import { getCustomerVersion, isUsVersion } from "./constants";

// Every external link of the app lives here: the customer's own site, its privacy policy and
// its help page. Nothing else in the codebase may hardcode an http(s) address, so adding a
// build or moving a page is a single edit in this table.
//
// The help page is the only one that can differ per language: `default` is what every language
// gets that has no entry of its own.

type HelpLinks = { default: string } & { [language: string]: string };

type VersionLinks = {
  // where the logo in the header takes the visitor
  home: string;
  // shown under the terms checkbox of the offer form; null means there is no page to link yet
  privacyPolicy: string | null;
  help: HelpLinks;
};

const GUMTECH: VersionLinks = {
  home: "https://gum-tech.de/",
  privacyPolicy: "https://gum-tech.de/datenschutzerklaerung/",
  help: { default: "https://gum-tech.de/kalkulatorhilfe" },
};

const PROMERA: VersionLinks = {
  home: "https://www.promeram.com/",
  privacyPolicy: null,
  help: { default: "https://gum-tech.de/kalkulatorhilfe" },
};

export const LINKS: Record<string, VersionLinks> = {
  gumtech: GUMTECH,
  lilowersum: {
    home: "https://www.lilowersum.de/",
    privacyPolicy: "https://www.lilowersum.de/Shop-Informationen/Datenschutzerklaerung/",
    help: {
      de: "https://www.lilowersum.de/EDITOR-Kalkulator-HILFE/",
      default: "https://www.lilowersum.de/EDITOR-calculator-HELP/",
    },
  },
  promera: PROMERA,
  // ?version=gumtech-us stands in for the US domain, which is a Promera one
  "gumtech-us": PROMERA,
};

const linksOf = (version?: string): VersionLinks => LINKS[version || getCustomerVersion()] || LINKS.gumtech;

// the US version is not a build (see isUsVersion), so its logo goes home whatever build it is:
// to the US domain it is served on, or to the first one when it is only tried with ?version=
const US_HOMES = ["https://freeareacalculator.promeram.com/", "https://freeareacalculator.promera.hu/"];

const usHomeOf = (hostname: string): string =>
  US_HOMES.find((home) => new URL(home).hostname === hostname) || US_HOMES[0];

export const getHomeLink = (version?: string, hostname: string = window.location.hostname): string =>
  isUsVersion() ? usHomeOf(hostname) : linksOf(version).home;

export const getPrivacyPolicyLink = (version?: string): string | null => linksOf(version).privacyPolicy;

export const getHelpLink = (language: string, version?: string): string => {
  const { help } = linksOf(version);
  return help[language] || help.default;
};
